import React from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale/fr';
import { CalendarIcon } from '@heroicons/react/24/outline';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css';

// Enregistrer la locale française
registerLocale('fr', fr);

interface DatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  className?: string;
  placeholderText?: string;
  dateFormat?: string;
  id?: string;
  disabled?: boolean;
  tabIndex?: number;
  /** Si true, sélectionne uniquement un mois (utilise showMonthYearPicker de react-datepicker) */
  monthOnly?: boolean;
  /** Variante compacte : hauteur réduite à h-7 (28px), padding et police adaptés (utile en header) */
  size?: 'default' | 'compact';
}

export const DatePicker: React.FC<DatePickerProps> = ({
  selected,
  onChange,
  className = '',
  placeholderText = 'Sélectionner une date',
  dateFormat,
  id,
  disabled = false,
  tabIndex,
  monthOnly = false,
  size = 'default',
}) => {
  const effectiveDateFormat = dateFormat ?? (monthOnly ? 'MMMM yyyy' : 'dd/MM/yyyy');
  const isCompact = size === 'compact';
  const baseClasses = isCompact
    ? 'block w-full h-7 px-2 py-0 pr-7 text-xs font-medium rounded-lg border shadow-sm transition-all duration-200 leading-none'
    : 'block w-full h-10 px-4 py-2 pr-12 text-[13px] rounded-token-md border-2 shadow-sm transition-all duration-200';
  const enabledClasses = isCompact
    ? 'border-gray-300 bg-white text-gray-700 hover:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer'
    : 'border-default bg-surface hover:border-default focus:outline-none focus:ring-2 focus:ring-token-focus focus:ring-offset-token-focus cursor-pointer';
  const disabledClasses =
    'border-default bg-panel text-muted cursor-not-allowed opacity-60 pointer-events-none';

  return (
    <div className={`relative w-full ${disabled ? 'pointer-events-none' : ''}`}>
      <ReactDatePicker
        id={id}
        selected={selected}
        onChange={onChange}
        dateFormat={effectiveDateFormat}
        placeholderText={placeholderText}
        locale="fr"
        disabled={disabled}
        tabIndex={disabled ? -1 : tabIndex}
        className={`${baseClasses} ${disabled ? disabledClasses : enabledClasses} ${className}`}
        calendarClassName="custom-datepicker-calendar"
        wrapperClassName="w-full"
        showPopperArrow={false}
        popperClassName="react-datepicker-popper-custom"
        popperPlacement="bottom-end"
        showMonthDropdown={!monthOnly}
        showYearDropdown={!monthOnly}
        dropdownMode="scroll"
        showMonthYearPicker={monthOnly}
      />
      <div
        className={`absolute ${isCompact ? 'right-2' : 'right-4'} top-1/2 -translate-y-1/2 pointer-events-none`}
      >
        <CalendarIcon className={`${isCompact ? 'h-3.5 w-3.5' : 'h-5 w-5'} text-muted`} />
      </div>
    </div>
  );
};
